import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import { Get_LeadIndicatorFunnel, Get_TargetFunnel, Get_KiiKpiRelationDetailsById, Add_Update_KiiKpiRelation, Get_KiiKpiRelationDetails } from "../../services/userAPI";
import { Select } from "antd";
import { message } from "antd";
import DeleteIcon from "../../Images/deleteButton-icon.png";

const scopedStyles = `
  .rl-lead-dropdown .ant-select-selector {
    border-color: #d9d9d9 !important;
    box-shadow: none !important;
  }
  .rl-lead-dropdown.ant-select-focused .ant-select-selector {
    border-color: #d9d9d9 !important;
    box-shadow: none !important;
  }
  .rl-lead-dropdown:hover .ant-select-selector {
    border-color: #d9d9d9 !important;
  }
`;

const RoleLeadIndicatorSelection = ({ onBack, roleId, targetFunnelName, onSaveSuccess }) => {
    const location = useLocation();
    const finalRoleId = roleId || location.state?.roleId;

    // Fetch lead indicators
    const { data: leadIndicatorData } = useQuery(Get_LeadIndicatorFunnel, {
        variables: { roleId: finalRoleId },
        skip: !finalRoleId,
        fetchPolicy: "network-only",
    });

    // Fetch target funnel data
    const { data: targetFunnelData } = useQuery(Get_TargetFunnel, {
        variables: { roleId: finalRoleId },
        skip: !finalRoleId,
        fetchPolicy: "network-only",
    });

    const { data: relationData, refetch } = useQuery(Get_KiiKpiRelationDetails, {
        variables: { roleId: finalRoleId },
        skip: !finalRoleId,
        fetchPolicy: "network-only",
        onError: (error) => {
            console.error("GraphQL Error:", error);
        },
    });

    const [deleteKpiKiiRelation] = useMutation(Get_KiiKpiRelationDetailsById);
    const [addUpdateKiiKpiRelation, { loading: saveLoading }] = useMutation(Add_Update_KiiKpiRelation);
    const [selectedIndicators, setSelectedIndicators] = useState({});
    const [weightages, setWeightages] = useState({});
    const [dropdownOpen, setDropdownOpen] = useState({});
    const [errors, setErrors] = useState({});
    const [fetchedRelations, setFetchedRelations] = useState([]);

    // Process relation data when it's fetched
    useEffect(() => {
        if (relationData?.getKiiKpiRelationDetails) {
            setFetchedRelations(relationData.getKiiKpiRelationDetails);

            // Initialize weightages from fetched data
            const initialWeightages = {};
            relationData.getKiiKpiRelationDetails.forEach((item) => {
                if (item.kiiId && item.id) {
                    initialWeightages[`${item.kpiId}-${item.kiiId}-${item.id}`] = item.relationKpiKii?.toString() || "";
                }
            });
            setWeightages((prev) => ({ ...prev, ...initialWeightages }));
        }
    }, [relationData]);

    // Helper function to group relations by KPI ID
    const getRelationsByKpiId = (kpiId) => {
        return fetchedRelations.filter((relation) => relation.kpiId === kpiId);
    };

    // Combine fetched relations and selected indicators for display
    const getIndicatorsForDisplay = (funnelId) => {
        const relations = getRelationsByKpiId(funnelId);
        const selected = selectedIndicators[funnelId] || [];

        // Map fetched relations
        const relationIndicators = relations.map((relation) => ({
            id: relation.id,
            kiiId: relation.kiiId,
            name: relation.kiiName,
            weightageKey: `${funnelId}-${relation.kiiId}-${relation.id}`,
            isFetched: true,
        }));

        // Map selected indicators (not in fetched relations)
        const selectedIndicatorsData = selected
            .filter((name) => !relations.some((rel) => rel.kiiName?.trim().toLowerCase() === name.trim().toLowerCase()))
            .map((name) => {
                const indicator = leadIndicatorData?.getKiisForRole.find(
                    (i) => i.name?.trim().toLowerCase() === name.trim().toLowerCase()
                );
                return {
                    id: null, // No relation ID yet (newly selected)
                    kiiId: indicator?.id,
                    name,
                    weightageKey: `${funnelId}-${name}`,
                    isFetched: false,
                };
            });

        return [...relationIndicators, ...selectedIndicatorsData];
    };

    const handleIndicatorChange = (funnelId, checkedValues) => {
        setSelectedIndicators((prev) => ({
            ...prev,
            [funnelId]: checkedValues,
        }));

        // Remove error message when a value is selected
        if (checkedValues.length > 0 || getRelationsByKpiId(funnelId).length > 0) {
            setErrors((prev) => ({ ...prev, [funnelId]: "" }));
        }

        // Set default weightages for newly selected indicators
        checkedValues.forEach((value) => {
            const key = `${funnelId}-${value}`;
            if (!weightages[key]) {
                setWeightages((prev) => ({
                    ...prev,
                    [key]: "",
                }));
            }
        });

        // Clear weightages for deselected indicators
        const previousIndicators = selectedIndicators[funnelId] || [];
        const removedIndicators = previousIndicators.filter((indicator) => !checkedValues.includes(indicator));
        removedIndicators.forEach((indicator) => {
            const key = `${funnelId}-${indicator}`;
            setWeightages((prev) => {
                const newWeightages = { ...prev };
                delete newWeightages[key];
                return newWeightages;
            });
        });
    };

    const handleSubmit = async () => {
        let hasError = false;
        let newErrors = {};

        // Check if at least one indicator is selected per funnel
        targetFunnelData?.getKpisForRole.forEach((funnel) => {
            if (
                (!selectedIndicators[funnel.id] || selectedIndicators[funnel.id].length === 0) &&
                getRelationsByKpiId(funnel.id).length === 0
            ) {
                newErrors[funnel.id] = `Please select at least one lead indicator for ${funnel.name}.`;
                hasError = true;
            }
        });

        setErrors(newErrors);

        if (!hasError) {
            // Prepare data for mutation
            const mutationPromises = [];

            // Process newly selected indicators
            targetFunnelData?.getKpisForRole.forEach((funnel) => {
                selectedIndicators[funnel.id]?.forEach((indicatorName) => {
                    const indicator = leadIndicatorData?.getKiisForRole.find(
                        (i) => i.name?.trim().toLowerCase() === indicatorName.trim().toLowerCase()
                    );

                    if (indicator) {
                        const weightage = weightages[`${funnel.id}-${indicatorName}`] || "";
                        mutationPromises.push(
                            addUpdateKiiKpiRelation({
                                variables: {
                                    data: [
                                        {
                                            kpiId: funnel.id,
                                            kiiId: indicator.id,
                                            relationKpiKii: weightage ? parseFloat(weightage) : 1,
                                            kpiKiiRelationId: null, // For new relations
                                        },
                                    ],
                                },
                            })
                        );
                    }
                });
            });

            // Update existing relations with new weightages
            fetchedRelations.forEach((relation) => {
                const weightageKey = `${relation.kpiId}-${relation.kiiId}-${relation.id}`;
                const newWeightage = weightages[weightageKey];

                if (newWeightage && parseFloat(newWeightage) !== relation.relationKpiKii) {
                    mutationPromises.push(
                        addUpdateKiiKpiRelation({
                            variables: {
                                data: [
                                    {
                                        kpiId: relation.kpiId,
                                        kiiId: relation.kiiId,
                                        relationKpiKii: parseFloat(newWeightage),
                                        kpiKiiRelationId: relation.id, // For updating existing relations
                                    },
                                ],
                            },
                        })
                    );
                }
            });

            try {
                await Promise.all(mutationPromises);
                await refetch();

                setSelectedIndicators({});
                message.success("Correlation Configuration is successful!");
                if (onSaveSuccess) {
                    onSaveSuccess();
                } else if (onBack) {
                    onBack();
                }
            } catch (error) {
                console.error("Error updating indicators:", error);
                message.error("An error occurred while saving.");
            }
        }
    };

    // Helper function to delete a fetched relation
    const handleDeleteRelation = async (relation) => {
        try {
            const { data } = await deleteKpiKiiRelation({
                variables: {
                    kpiKiiRelation: relation.id,
                },
            });

            if (data?.deleteKpiKiiRelation?.success) {
                message.success("Indicator deleted successfully!");
                await refetch();
            } else {
                message.error("Failed to delete indicator.");
            }
        } catch (error) {
            console.error("Error deleting relation:", error);
            message.error("An error occurred while deleting.");
        }
    };

    // Helper function to remove a selected (non-fetched) indicator
    const handleRemoveSelectedIndicator = (funnelId, indicatorName) => {
        const newSelected = selectedIndicators[funnelId].filter((name) => name !== indicatorName);
        handleIndicatorChange(funnelId, newSelected);
    };

    return (
        <div className="w-full pb-5">
            <style>{scopedStyles}</style>

            {targetFunnelData?.getKpisForRole ? (
                targetFunnelData.getKpisForRole.map((funnel) => {
                    const indicatorsForDisplay = getIndicatorsForDisplay(funnel.id);

                    return (
                        <div
                            key={funnel.id}
                            className="mb-4 rounded-[8px] border border-[#ddd] bg-white p-5"
                        >
                            <div className="mb-2.5 flex items-center justify-between">
                                <span className="font-bold">{funnel.name}</span>
                                <Select
                                    mode="multiple"
                                    placeholder="Select Lead Indicators"
                                    className="rl-lead-dropdown"
                                    style={{ width: "300px" }}
                                    value={[]}
                                    showArrow={true}
                                    onChange={(values) => handleIndicatorChange(funnel.id, values)}
                                    open={dropdownOpen[funnel.id]}
                                    onDropdownVisibleChange={(open) => setDropdownOpen({ ...dropdownOpen, [funnel.id]: open })}
                                    dropdownRender={(menu) => (
                                        <div>
                                            {leadIndicatorData?.getKiisForRole
                                                ?.filter((indicator) => {
                                                    const indicatorName = indicator?.name?.trim().toLowerCase();
                                                    if (!indicatorName) return false;

                                                    const inFetchedRelations = getRelationsByKpiId(funnel.id)
                                                        .map((relation) => relation.kiiName?.trim().toLowerCase())
                                                        .filter((name) => name != null);

                                                    return !inFetchedRelations.includes(indicatorName);
                                                })
                                                ?.map((indicator) => (
                                                    <div
                                                        key={indicator.id}
                                                        className="flex cursor-pointer items-center px-3 py-2"
                                                        onClick={() => {
                                                            const currentSelected = selectedIndicators[funnel.id] || [];
                                                            const indicatorName = indicator.name;
                                                            const isSelected = currentSelected.includes(indicatorName);
                                                            const newSelected = isSelected
                                                                ? currentSelected.filter((name) => name !== indicatorName)
                                                                : [...currentSelected, indicatorName];
                                                            handleIndicatorChange(funnel.id, newSelected);
                                                        }}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={(selectedIndicators[funnel.id] || []).includes(indicator.name)}
                                                            onChange={() => { }}
                                                            className="mr-2"
                                                        />
                                                        <span>{indicator.name}</span>
                                                    </div>
                                                ))}
                                        </div>
                                    )}
                                />
                            </div>

                            {errors[funnel.id] && <p className="mt-1 text-[13px] text-[#CC0000]">{errors[funnel.id]}</p>}

                            <div className="flex justify-between rounded-[5px] bg-[#eeeeee] p-2.5">
                                <span>Lead Indicators</span>
                            </div>

                            {/* Display indicators (fetched and selected) */}
                            {indicatorsForDisplay.length === 0 ? (
                                <p className="mt-2.5 text-[#888]">No Lead Indicators Selected</p>
                            ) : (
                                <div>
                                    {indicatorsForDisplay.map((indicator) => (
                                        <div
                                            key={indicator.weightageKey}
                                            className="flex items-center justify-between gap-2.5 border-b border-[#ddd] py-2"
                                        >
                                            <span className="flex-1">{indicator.name}</span>
                                            {indicator.isFetched ? (
                                                <img
                                                    src={DeleteIcon}
                                                    onClick={() => handleDeleteRelation({ id: indicator.id, kpiId: funnel.id, kiiId: indicator.kiiId })}
                                                    className="h-[18px] w-[18px] cursor-pointer"
                                                    alt="Delete"
                                                />
                                            ) : (
                                                <img
                                                    src={DeleteIcon}
                                                    onClick={() => handleRemoveSelectedIndicator(funnel.id, indicator.name)}
                                                    className="h-[18px] w-[18px] cursor-pointer"
                                                    alt="Delete"
                                                />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })
            ) : (
                <p>No target funnels found.</p>
            )}

            <div className="mb-5 mt-5 flex justify-end">
                <button
                    onClick={handleSubmit}
                    disabled={saveLoading}
                    className="flex h-[42px] w-[82px] items-center justify-center rounded-md border-0 bg-[#AD0101] text-base font-medium text-white transition-colors duration-300 hover:bg-[#8C0101] disabled:opacity-60"
                >
                    {saveLoading ? <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white"></span> : "Save"}
                </button>
            </div>
        </div>
    );
};

export default RoleLeadIndicatorSelection;
